import { useEffect, useRef, useState } from 'react';
import NewBadge from '../primitives/NewBadge';
import MediaLoadingSpinner from '../primitives/MediaLoadingSpinner';
import { localizedField } from '../../locales/content';
import { hapticImpact } from '../../utils/haptics';
import TitleImageOverlay from './TitleImageOverlay';

function hashString(value) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function stableRandomize(values, seedBase) {
  const result = [...values];
  let seed = hashString(seedBase) || 1;

  for (let i = result.length - 1; i > 0; i -= 1) {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    const j = seed % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

function buildTileSources(group) {
  const presetImages = Array.isArray(group?.presets)
    ? group.presets.map((preset) => preset?.homeThumbUrl || preset?.image).filter(Boolean)
    : [];

  const seedBase = group?.id || group?.name || 'quad';
  const unique = stableRandomize([...new Set(presetImages)], seedBase).slice(0, 4);
  const fallback = group?.cover || unique[0] || null;

  if (!fallback) return [];
  if (unique.length === 0) return [fallback, fallback, fallback, fallback];

  const tiles = [...unique];
  while (tiles.length < 4) {
    tiles.push(tiles[tiles.length - 1] || fallback);
  }
  return tiles.slice(0, 4);
}

export default function PresetGroupCardQuad({ group, onClick, fluid = false }) {
  if (!group) return null;

  const { titleImage, isNew } = group;
  const name = localizedField(group, 'name') || group.name;
  const tileSources = buildTileSources(group);
  const tileRefs = useRef([]);
  const [loadedTiles, setLoadedTiles] = useState(() => new Set());

  useEffect(() => {
    tileRefs.current = tileRefs.current.slice(0, tileSources.length);
    setLoadedTiles(new Set());

    const markCachedTilesLoaded = () => {
      setLoadedTiles((current) => {
        const next = new Set(current);
        tileSources.forEach((_, index) => {
          if (tileRefs.current[index]?.complete) next.add(index);
        });
        return next.size === current.size ? current : next;
      });
    };

    const frame = requestAnimationFrame(markCachedTilesLoaded);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') markCachedTilesLoaded();
    };

    window.addEventListener('pageshow', markCachedTilesLoaded);
    window.addEventListener('focus', markCachedTilesLoaded);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('pageshow', markCachedTilesLoaded);
      window.removeEventListener('focus', markCachedTilesLoaded);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [tileSources.join('|'), tileSources.length]);

  if (tileSources.length === 0) return null;
  const allTilesLoaded = loadedTiles.size >= tileSources.length;

  return (
    <button
      type="button"
      onClick={() => {
        hapticImpact('light');
        if (onClick) onClick(group);
      }}
      className={[
        'relative flex-shrink-0 text-left p-0 border-0 bg-transparent',
        fluid ? 'aspect-square w-full' : '',
      ].filter(Boolean).join(' ')}
      aria-label={name}
    >
      <div
        className={[
          'relative rounded-home-24 overflow-hidden bg-surface-2',
          fluid ? 'h-full w-full' : 'h-40 w-40',
        ].join(' ')}
      >
        <div className="grid grid-cols-2 grid-rows-2 gap-0.5 w-full h-full bg-bg-app">
          {tileSources.map((src, index) => (
            <img
              // eslint-disable-next-line react/no-array-index-key
              key={`${src}-${index}`}
              ref={(node) => {
                tileRefs.current[index] = node;
              }}
              src={src}
              alt=""
              aria-hidden="true"
              data-role="quad-tile"
              className="w-full h-full object-cover pointer-events-none"
              loading="lazy"
              decoding="async"
              draggable={false}
              onLoad={() => {
                setLoadedTiles((current) => {
                  const next = new Set(current);
                  next.add(index);
                  return next;
                });
              }}
              onError={() => {
                setLoadedTiles((current) => {
                  const next = new Set(current);
                  next.add(index);
                  return next;
                });
              }}
            />
          ))}
        </div>
        {!allTilesLoaded && <MediaLoadingSpinner />}
        <div
          aria-hidden="true"
          className="absolute inset-0 z-[1]"
          data-native-menu-shield="true"
        />
        {isNew && <NewBadge className="absolute top-0 right-0 z-[2]" coverRadius={24} />}
        <TitleImageOverlay src={titleImage} group={group} className="z-[2]" />
      </div>
    </button>
  );
}
